import type { Pool } from "pg";

import {
  CLAMP_FORECAST_ADD_TO_PO_SPECIAL_INSTRUCTIONS_MAX_LEN,
  truncateClampForecastAddToPoSpecialInstructions,
} from "./clamp-forecast-add-to-po-options.js";
import { SQL_PO_DISPLAY_NUMBER_EXPR } from "./purchase-order-display.js";
import { SQL_PO_IS_OPEN_FOR_PLANNING } from "./purchase-order-status.js";
import type { LocationCode } from "./types.js";

export type SafetyStockAddToPoOption = {
  purchase_order_id: number;
  mv_number: string | null;
  supplier_name: string | null;
  line_count: number;
  special_instructions: string | null;
  is_hub_draft: boolean;
};

export function formatSafetyStockAddToPoOptionLabel(option: SafetyStockAddToPoOption): string {
  const numberLabel = option.mv_number ?? `PO #${option.purchase_order_id}`;
  const lineSuffix = option.line_count === 1 ? "" : ` (${option.line_count} lines)`;
  const instructions = option.special_instructions?.trim() ?? "";
  if (instructions === "") return `${numberLabel}${lineSuffix}`;
  return `${numberLabel}${lineSuffix} — ${truncateClampForecastAddToPoSpecialInstructions(
    instructions,
    CLAMP_FORECAST_ADD_TO_PO_SPECIAL_INSTRUCTIONS_MAX_LEN,
  )}`;
}

export async function loadSafetyStockAddToPoOptions(
  pool: Pool,
  locationCode: LocationCode,
  _userId: string,
): Promise<SafetyStockAddToPoOption[]> {
  const result = await pool.query<{
    purchase_order_id: string;
    mv_number: string | null;
    supplier_name: string | null;
    line_count: string;
    special_instructions: string | null;
    is_hub_draft: boolean;
  }>(
    `select public.purchase_order.id::bigint as purchase_order_id,
            (${SQL_PO_DISPLAY_NUMBER_EXPR}) as mv_number,
            supplier.data.mv_supplier_name as supplier_name,
            count(public.purchase_order_item.id)::int as line_count,
            nullif(trim(both from public.purchase_order.special_instructions), '') as special_instructions,
            false as is_hub_draft
       from public.purchase_order
       left join supplier.data
         on supplier.data.id = public.purchase_order.supplier_id
       left join public.purchase_order_item
         on public.purchase_order_item.purchase_order_id = public.purchase_order.id
       join public.location
         on public.location.id = public.purchase_order.location_id
      where public.location.mrp_site_code = $1
        and (
          (
            public.purchase_order.system_of_record = 'hub'
            and ${SQL_PO_IS_OPEN_FOR_PLANNING}
          )
          or (
            public.purchase_order.system_of_record is distinct from 'hub'
            and public.purchase_order.mv_id is not null
            and lower(trim(coalesce(public.purchase_order.mv_status, '')))
                not in ('cancelled', 'canceled', 'closed', 'fullyinvoiced')
          )
        )
      group by public.purchase_order.id,
               public.purchase_order.mv_number,
               public.purchase_order.number,
               supplier.data.mv_supplier_name,
               public.purchase_order.special_instructions
      order by coalesce((${SQL_PO_DISPLAY_NUMBER_EXPR})::text, public.purchase_order.id::text)`,
    [locationCode],
  );
  return result.rows.map((row) => ({
    purchase_order_id: Number(row.purchase_order_id),
    mv_number: row.mv_number == null ? null : String(row.mv_number),
    supplier_name: row.supplier_name == null ? null : String(row.supplier_name),
    line_count: Number(row.line_count),
    special_instructions: row.special_instructions == null ? null : String(row.special_instructions),
    is_hub_draft: Boolean(row.is_hub_draft),
  }));
}
